import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const MenuMaker = ({ data = [], text, align, isActive = false, onSelect }) => {
  const history = useHistory();
  const location = useLocation();
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuIdRef = useRef(`dropdown-menu-${Math.random().toString(36).slice(2)}`);
  const [isOpen, setIsOpen] = useState(false);

  // Never mutate `data` (it's a prop); create a safe copy for slicing.
  const items = useMemo(() => (Array.isArray(data) ? [...data] : []), [data]);
  const firstItem = items[0];
  const lastItem = items[items.length - 1];
  const middleItems = items.slice(1, -1);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleItemAction = (item) => {
    if (!item) return;
    if (typeof item.onClick === 'function') item.onClick();
    else if (item.url) history.push(item.url);
    setIsOpen(false);
    if (typeof onSelect === 'function') onSelect();
  };

  return (
    <div
      className={`dropdown relative z-20 inline-block ${isOpen ? 'is-open' : ''}`}
      ref={containerRef}>
      <button
        type="button"
        className={`inline-flex items-center rounded px-2 py-1 text-gray-200 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white ${
          isActive ? 'bg-gray-700 font-semibold text-white' : ''
        }`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuIdRef.current}
        ref={triggerRef}
        onClick={() => setIsOpen((v) => !v)}>
        {text}
      </button>

      <ul
        id={menuIdRef.current}
        role="menu"
        className={`dropdown-menu absolute ${isOpen ? 'block' : 'hidden'} min-w-[12rem] max-w-[18rem] w-max rounded-md bg-gray-700 py-1 text-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 ${
          align === 'right' ? 'left-auto right-0' : ''
        }`}>
        {items.length === 0 ? null : (
          <>
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="block w-full cursor-pointer truncate px-3 py-2 text-left text-sm leading-5 text-gray-100 hover:bg-gray-600 focus:bg-gray-600 focus:outline-none"
                onClick={() => handleItemAction(firstItem)}>
                {firstItem?.name}
              </button>
            </li>

            {middleItems.map((row, index) => (
              <li
                role="none"
                key={index}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleItemAction(row)}
                  className="block w-full cursor-pointer truncate px-3 py-2 text-left text-sm leading-5 text-gray-100 hover:bg-gray-600 focus:bg-gray-600 focus:outline-none">
                  {row?.name}
                </button>
              </li>
            ))}

            {items.length > 1 ? (
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full cursor-pointer truncate px-3 py-2 text-left text-sm leading-5 text-gray-100 hover:bg-gray-600 focus:bg-gray-600 focus:outline-none"
                  onClick={() => handleItemAction(lastItem)}>
                  {lastItem?.name}
                </button>
              </li>
            ) : null}
          </>
        )}
      </ul>
    </div>
  );
};

export default MenuMaker;
